export interface ScopedItemTypeDisplay {
  label: string;
  singularLabel?: string;
  description?: string;
}

export interface ScopedItemTypeQuerySpec {
  itemTypes: string[];
  itemSubtypes: string[];
}

const SCOPED_ITEM_TYPE_DISPLAY_OVERRIDES: Record<string, Record<string, ScopedItemTypeDisplay>> = {
  'advanced-manufacturing': {
    production_order: {
      label: 'Production Orders',
      singularLabel: 'Production Order',
      description: 'Manufacturing jobs and production orders',
    },
    bom: {
      label: 'Bills of Materials',
      singularLabel: 'Bill of Materials',
      description: 'Materials structures, revisions, and cost rollups',
    },
    manufacturing_stage: {
      label: 'Production Stages',
      singularLabel: 'Production Stage',
      description: 'Tracked shop-floor stages and operations',
    },
    outsourced_operation: {
      label: 'Outsourced Operations',
      singularLabel: 'Outsourced Operation',
      description: 'Supplier-managed process steps and turnaround tracking',
    },
    quality_check: {
      label: 'Quality Checks',
      singularLabel: 'Quality Check',
      description: 'Inspection checkpoints, QC tasks, and verification work',
    },
    ncr: {
      label: 'NCRs',
      singularLabel: 'NCR',
      description: 'Non-conformance reports and corrective actions',
    },
    tooling: {
      label: 'Tooling',
      singularLabel: 'Tool',
      description: 'Tooling, jigs, fixtures, and equipment records',
    },
  },
  'legal-services': {
    project: {
      label: 'Cases',
      singularLabel: 'Matter',
      description: 'Legal matters, case files, and mandates',
    },
    matter: {
      label: 'Cases',
      singularLabel: 'Matter',
      description: 'Legal matters, case files, and mandates',
    },
    appointment: {
      label: 'Court Dates',
      singularLabel: 'Court Date',
      description: 'Hearings, deadlines, and court diary entries',
    },
    court_date: {
      label: 'Court Dates',
      singularLabel: 'Court Date',
      description: 'Hearings, deadlines, and court diary entries',
    },
    task: {
      label: 'Tasks',
      singularLabel: 'Task',
      description: 'Matter-related legal tasks and actions',
    },
    contract: {
      label: 'Contracts',
      singularLabel: 'Contract',
      description: 'Drafted, negotiated, and uploaded legal contracts',
    },
    trust_transaction: {
      label: 'Trust Transactions',
      singularLabel: 'Trust Transaction',
      description: 'Trust account receipts, payments, and reconciliations',
    },
  },
};

const SCOPED_ITEM_TYPE_QUERY_OVERRIDES: Record<string, Record<string, ScopedItemTypeQuerySpec>> = {
  'advanced-manufacturing': {
    job: {
      itemTypes: ['job'],
      itemSubtypes: ['production_order'],
    },
    production_order: {
      itemTypes: ['job'],
      itemSubtypes: ['production_order'],
    },
    manufacturing_stage: {
      itemTypes: ['job'],
      itemSubtypes: ['manufacturing_stage'],
    },
    outsourced_operation: {
      itemTypes: ['job'],
      itemSubtypes: ['outsourced_operation'],
    },
  },
};

function normalizeItemType(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function getScopedItemTypeAliases(itemType: string, moduleContext?: string | null): string[] {
  const normalizedContext = normalizeItemType(moduleContext);

  if (normalizedContext === 'legal-services') {
    if (itemType === 'project' || itemType === 'matter') {
      return ['project', 'matter'];
    }

    if (itemType === 'appointment' || itemType === 'court_date') {
      return ['appointment', 'court_date'];
    }
  }

  return itemType ? [itemType] : [];
}

export function normalizeSolutionItemTypes(rawItemTypes: unknown): string[] {
  const sourceValues = Array.isArray(rawItemTypes)
    ? rawItemTypes
    : typeof rawItemTypes === 'string'
      ? [rawItemTypes]
      : [];

  const normalizedTypes = new Set<string>();

  sourceValues.forEach((value) => {
    if (typeof value !== 'string') {
      return;
    }

    value
      .replace(/[{}]/g, '')
      .split(',')
      .map((itemType) => itemType.trim())
      .filter(Boolean)
      .forEach((itemType) => normalizedTypes.add(itemType));
  });

  return Array.from(normalizedTypes);
}

export function expandScopedEquivalentItemTypes(
  rawItemTypes: Iterable<string>,
  moduleContext?: string | null,
): string[] {
  const expandedTypes = new Set<string>();

  for (const rawItemType of rawItemTypes) {
    const itemType = normalizeItemType(rawItemType);
    if (!itemType) {
      continue;
    }

    getScopedItemTypeAliases(itemType, moduleContext).forEach((alias) => expandedTypes.add(alias));
  }

  return Array.from(expandedTypes);
}

export function getScopedItemTypeQuerySpec(
  itemType: string | null | undefined,
  moduleContext?: string | null,
): ScopedItemTypeQuerySpec {
  const normalizedContext = normalizeItemType(moduleContext);
  const normalizedType = normalizeItemType(itemType);

  if (!normalizedType) {
    return { itemTypes: [], itemSubtypes: [] };
  }

  const override = normalizedContext
    ? SCOPED_ITEM_TYPE_QUERY_OVERRIDES[normalizedContext]?.[normalizedType]
    : null;

  if (override) {
    return {
      itemTypes: Array.from(new Set(override.itemTypes.map((value) => normalizeItemType(value)).filter(Boolean))),
      itemSubtypes: Array.from(new Set(override.itemSubtypes.map((value) => normalizeItemType(value)).filter(Boolean))),
    };
  }

  return {
    itemTypes: getScopedItemTypeAliases(normalizedType, normalizedContext),
    itemSubtypes: [],
  };
}

export function combineScopedItemTypeQuerySpecs(
  rawItemTypes: Iterable<string>,
  moduleContext?: string | null,
): ScopedItemTypeQuerySpec {
  const itemTypes = new Set<string>();
  const itemSubtypes = new Set<string>();

  for (const rawItemType of rawItemTypes) {
    const spec = getScopedItemTypeQuerySpec(rawItemType, moduleContext);
    spec.itemTypes.forEach((value) => itemTypes.add(value));
    spec.itemSubtypes.forEach((value) => itemSubtypes.add(value));
  }

  return {
    itemTypes: Array.from(itemTypes),
    itemSubtypes: Array.from(itemSubtypes),
  };
}

export function getScopedItemTypeDisplay(
  itemType: string | null | undefined,
  moduleContext?: string | null,
): ScopedItemTypeDisplay | null {
  const normalizedContext = normalizeItemType(moduleContext);
  const normalizedType = normalizeItemType(itemType);

  if (!normalizedContext || !normalizedType) {
    return null;
  }

  return SCOPED_ITEM_TYPE_DISPLAY_OVERRIDES[normalizedContext]?.[normalizedType] || null;
}

export function getScopedCreateItemType(
  itemType: string | null | undefined,
  moduleContext?: string | null,
): string {
  const normalizedContext = normalizeItemType(moduleContext);
  const normalizedType = normalizeItemType(itemType);

  if (normalizedContext === 'advanced-manufacturing') {
    if (normalizedType === 'job' || normalizedType === 'production_order' || normalizedType === 'manufacturing_stage' || normalizedType === 'outsourced_operation') {
      return 'job';
    }
  }

  if (normalizedContext === 'legal-services') {
    if (normalizedType === 'project' || normalizedType === 'matter') {
      return 'matter';
    }

    if (normalizedType === 'appointment' || normalizedType === 'court_date') {
      return 'court_date';
    }
  }

  return normalizedType;
}